import { DEFAULT_MIN_COUNT, MINIMUM_ORDER_QUANTITY_TYPE } from '../../constant/product-option';
import { GetCatalogProductDetailPageOption_pdp_option_info as OptionInfo } from '../../graphql/select-option_types.2';
import type { ProductOptionConfig } from '../../type/common.type';
import { checkZigzinOrVendorFlexFulfillmentType } from '../checkers/zigzin-checker';

type ProductMetadata = ReturnType<typeof extractBasicCatalogInfo> &
  ReturnType<typeof extractMetaMatchingInfo> &
  ReturnType<typeof extractPriceInfo> &
  ReturnType<typeof extractQuantityInfo> &
  ReturnType<typeof extractBogoInfo> &
  ReturnType<typeof extractSizeRecommendationInfo> &
  ReturnType<typeof extractProductFlags> & {
    promotion_info: OptionInfo['catalog_product']['promotion_info'] | null;
    store_deal_banner_guide_text: string;
    notice_state: ReturnType<typeof createNoticeState>;
  };

/**
 * 기본 카탈로그 정보 추출
 */
const extractBasicCatalogInfo = (option_info: OptionInfo | null) => {
  const catalog_product = option_info?.catalog_product;

  return {
    shop_id: catalog_product?.shop_id ?? '',
    catalog_product,
    catalog_product_id: catalog_product?.id ?? '',
    matching_catalog_product_id: catalog_product?.matching_catalog_product_info?.id ?? '',
  };
};

/**
 * 주문 수량 관련 정보 추출
 */
const extractQuantityInfo = (catalog_product: OptionInfo['catalog_product'], config?: ProductOptionConfig) => {
  const minimum_order_quantity_type = catalog_product?.minimum_order_quantity_type ?? '';
  const minimum_order_quantity = catalog_product?.minimum_order_quantity ?? 0;
  const maximum_order_quantity = catalog_product?.maximum_order_quantity ?? 0;

  const min_order_quantity = config?.disableMinimumOrderQuantity ? DEFAULT_MIN_COUNT : minimum_order_quantity;

  return {
    minimum_order_quantity_type,
    minimum_order_quantity,
    maximum_order_quantity,
    min_order_quantity,
  };
};

/**
 * BOGO 프로모션 정보 추출
 */
const extractBogoInfo = (catalog_product: OptionInfo['catalog_product']) => {
  const bogo_info = catalog_product?.promotion_info?.bogo_info ?? null;
  const bogo_required_quantity = bogo_info?.required_quantity ?? 0;

  return {
    bogo_info,
    bogo_required_quantity,
    is_bogo: Boolean(bogo_required_quantity),
  };
};

/**
 * 사이즈 추천 정보 추출
 */
const extractSizeRecommendationInfo = (catalog_product: OptionInfo['catalog_product']) => {
  const size_recommendation = catalog_product?.option_size_recommendation ?? null;

  return {
    size_recommendation,
  };
};

/**
 * 가격 할인 정보 추출
 */
const extractPriceInfo = (catalog_product: OptionInfo['catalog_product']) => {
  const first_order_discount = catalog_product?.product_price?.first_order_discount ?? null;
  const first_order_discount_amount = first_order_discount?.discount_amount ?? 0;
  const product_final_discount_price = catalog_product?.product_price.final_discount_info.discount_price ?? 0;
  const matching_product_final_discount_price =
    catalog_product?.matching_catalog_product_info?.product_price.final_discount_info.discount_price ?? 0;

  return {
    first_order_discount,
    first_order_discount_amount,
    product_final_discount_price,
    matching_product_final_discount_price,
    has_first_order_discount: Boolean(first_order_discount_amount),
  };
};

/**
 * 상품 플래그 정보 추출
 */
const extractProductFlags = (
  catalog_product: OptionInfo['catalog_product'],
  option_info: OptionInfo | null,
  disabled_counter: boolean | undefined,
) => {
  const minimum_order_quantity_type = catalog_product?.minimum_order_quantity_type ?? '';
  const is_order_quantity_product_type = minimum_order_quantity_type === MINIMUM_ORDER_QUANTITY_TYPE.PRODUCT;
  const is_order_quantity_item_type = minimum_order_quantity_type === MINIMUM_ORDER_QUANTITY_TYPE.ITEM;
  const is_zigzin_product = checkZigzinOrVendorFlexFulfillmentType(catalog_product?.fulfillment_type ?? null);
  const is_zigzin_matching_product = is_zigzin_product && Boolean(catalog_product?.matching_catalog_product_info);
  const is_general_matching_product = !is_zigzin_product && Boolean(catalog_product?.matching_catalog_product_info);

  const is_purchase_only_one_at_time = Boolean(option_info?.flags?.is_purchase_only_one_at_time);
  const has_store_deal_banner = Boolean(catalog_product?.store_deal_banner);

  return {
    is_order_quantity_product_type,
    is_order_quantity_item_type,
    is_zigzin_product,
    is_zigzin_matching_product,
    is_general_matching_product,
    is_purchase_only_one_at_time,
    has_store_deal_banner,
    disabled_counter_button: Boolean(disabled_counter || is_purchase_only_one_at_time || has_store_deal_banner),
  };
};

/**
 * 메타 매칭 정보 추출
 */
const extractMetaMatchingInfo = (catalog_product: OptionInfo['catalog_product']) => {
  return {
    meta_matching_pdp_url:
      catalog_product?.meta_catalog_product_info?.pdp_url ?? catalog_product?.matching_catalog_product_info?.pdp_url,
    meta_matching_product_id:
      catalog_product?.meta_catalog_product_info?.id ?? catalog_product?.matching_catalog_product_info?.id ?? '',
    meta_matching_browsing_type:
      catalog_product?.meta_catalog_product_info?.browsing_type ??
      catalog_product?.matching_catalog_product_info?.browsing_type,
    is_matching_link_available: Boolean(
      catalog_product?.meta_catalog_product_info?.is_able_to_buy ??
        catalog_product?.matching_catalog_product_info?.is_able_to_buy,
    ),
  };
};

/**
 * 알림 상태 정보 생성
 */
const createNoticeState = (
  has_first_order_discount: boolean,
  first_order_discount_amount: number,
  maximum_order_quantity: number,
  min_order_quantity: number,
  is_order_quantity_product_type: boolean,
  is_purchase_only_one_at_time: boolean,
  has_store_deal_banner: boolean,
  store_deal_banner_guide_text: string,
) => {
  const display_order_quantity_notice = Boolean(
    maximum_order_quantity || (is_order_quantity_product_type && min_order_quantity),
  );
  const display_order_quantity_recent_30days_note = Boolean(maximum_order_quantity && !is_purchase_only_one_at_time);

  return {
    first_order: {
      display: has_first_order_discount,
      first_order_discount_amount,
    },
    order_quantity: {
      display: display_order_quantity_notice,
      display_recent_30days_note: display_order_quantity_recent_30days_note,
      maximum_order_quantity,
      minimum_order_quantity: is_order_quantity_product_type ? min_order_quantity : 0,
    },
    store_deal: {
      display: has_store_deal_banner,
      text: store_deal_banner_guide_text,
    },
  };
};

/**
 * 기본 메타데이터 (catalog_product가 없을 때)
 *
 * ProductMetadata 타입을 명시하여 누락된 필드가 있으면 컴파일 에러 발생
 */
const createDefaultMetadata = (): ProductMetadata => ({
  // 기본 정보
  shop_id: '',
  catalog_product: undefined,
  catalog_product_id: '',
  matching_catalog_product_id: '',

  // 메타 매칭 정보
  ...createDefaultMetaMatchingInfo(),

  // 가격 정보
  ...createDefaultPriceInfo(),

  // 수량 정보
  ...createDefaultQuantityInfo(),

  // BOGO 정보
  ...createDefaultBogoInfo(),

  // 사이즈 추천 정보
  size_recommendation: null,

  // 상품 플래그
  ...createDefaultProductFlags(),

  // 추가 정보
  promotion_info: null,
  store_deal_banner_guide_text: '',
  notice_state: createNoticeState(false, 0, 0, DEFAULT_MIN_COUNT, false, false, false, ''),
});

const createDefaultMetaMatchingInfo = () => ({
  meta_matching_pdp_url: undefined,
  meta_matching_product_id: '',
  meta_matching_browsing_type: undefined,
  is_matching_link_available: false,
});

const createDefaultPriceInfo = () => ({
  first_order_discount: null,
  first_order_discount_amount: 0,
  product_final_discount_price: 0,
  matching_product_final_discount_price: 0,
  has_first_order_discount: false,
});

const createDefaultQuantityInfo = () => ({
  minimum_order_quantity_type: '',
  minimum_order_quantity: 0,
  maximum_order_quantity: 0,
  min_order_quantity: DEFAULT_MIN_COUNT,
});

const createDefaultBogoInfo = () => ({
  bogo_info: null,
  bogo_required_quantity: 0,
  is_bogo: false,
});

const createDefaultProductFlags = () => ({
  is_order_quantity_product_type: false,
  is_order_quantity_item_type: false,
  is_zigzin_product: false,
  is_zigzin_matching_product: false,
  is_general_matching_product: false,
  is_purchase_only_one_at_time: false,
  has_store_deal_banner: false,
  disabled_counter_button: false,
});

/**
 * 상품 메타데이터 추출 메인 함수
 *
 * ProductMetadata 타입을 명시하여 누락된 필드가 있으면 컴파일 에러 발생
 */
export const productMetadataBuilder = (
  option_info: OptionInfo | null,
  config?: ProductOptionConfig,
): ProductMetadata => {
  const basic_info = extractBasicCatalogInfo(option_info);

  if (!basic_info.catalog_product) {
    return createDefaultMetadata();
  }

  const { shop_id, catalog_product, catalog_product_id, matching_catalog_product_id } = basic_info;

  // 각 도메인별 정보 추출
  const meta_matching_info = extractMetaMatchingInfo(catalog_product);
  const price_info = extractPriceInfo(catalog_product);
  const quantity_info = extractQuantityInfo(catalog_product, config);
  const bogo_info = extractBogoInfo(catalog_product);
  const size_recommendation_info = extractSizeRecommendationInfo(catalog_product);
  const product_flags = extractProductFlags(catalog_product, option_info, config?.disabled_counter);

  // 추가 정보
  const store_deal_banner_guide_text = catalog_product?.store_deal_banner?.guide_text ?? '';
  const promotion_info = catalog_product?.promotion_info ?? null;

  const notice_state = createNoticeState(
    price_info.has_first_order_discount,
    price_info.first_order_discount_amount,
    quantity_info.maximum_order_quantity,
    quantity_info.min_order_quantity,
    product_flags.is_order_quantity_product_type,
    product_flags.is_purchase_only_one_at_time,
    product_flags.has_store_deal_banner,
    store_deal_banner_guide_text,
  );

  // 모든 정보를 조합하여 반환 (ProductMetadata 타입으로 보장)
  return {
    // 기본 정보
    shop_id,
    catalog_product,
    catalog_product_id,
    matching_catalog_product_id,

    // 도메인별 정보 (순서대로)
    ...meta_matching_info,
    ...price_info,
    ...quantity_info,
    ...bogo_info,
    ...size_recommendation_info,
    ...product_flags,

    promotion_info,
    store_deal_banner_guide_text,
    notice_state,
  };
};
